<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { getFormColor, FORM_TYPE_LABELS } from '../../../composables/useReservationsCalendar.js'

const SINGLE_COL_THRESHOLD = 5  // víc formulářů → přejde na grid

const props = defineProps({
    open:  { type: Boolean, default: false },
    forms: { type: Array,   default: () => [] },
    // Pozice: { top, right } v px relativně k viewportu
    // Pokud není uvedena, je popover v top-right toolbaru
    anchorRect: { type: Object, default: null },
})

const emit = defineEmits(['close'])

// ─── Stav: 'picking' | { formId } ────────────────────────────────

const selectedForm = ref(null)

watch(() => props.open, (val) => {
    if (!val) selectedForm.value = null  // reset při zavření
})

function selectForm(form) {
    selectedForm.value = form
}

function goBack() {
    selectedForm.value = null
}

function openWidget() {
    router.visit(`/w/${selectedForm.value.slug}`)
    emit('close')
}

// ─── Pozice popovert ─────────────────────────────────────────────

const popoverStyle = ref({})

watch(() => [props.open, props.anchorRect], async ([open, rect]) => {
    if (!open) return
    await nextTick()
    if (rect) {
        popoverStyle.value = {
            top:   `${rect.bottom + 6}px`,
            right: `${window.innerWidth - rect.right}px`,
        }
    } else {
        // Fallback: pod topbarem, vpravo
        popoverStyle.value = { top: '70px', right: '24px' }
    }
}, { immediate: true })

// ─── Helpers ─────────────────────────────────────────────────────

function formTypeLabel(form) {
    return FORM_TYPE_LABELS[form.form_type?.name] ?? form.form_type?.name ?? ''
}

const isWide = computed(() => props.forms.length > SINGLE_COL_THRESHOLD)

// Skupiny po typech (pro wide mód)
const TYPE_ORDER = ['personal', 'service', 'rental', 'accommodation']
const formGroups = computed(() => {
    const map = {}
    for (const form of props.forms) {
        const key = form.form_type?.name ?? 'personal'
        ;(map[key] ??= []).push(form)
    }
    return TYPE_ORDER
        .filter(k => map[k]?.length)
        .map(k => ({ key: k, label: FORM_TYPE_LABELS[k], forms: map[k] }))
})
</script>

<template>
    <Teleport to="body">
        <!-- Scrim (průhledný, jen zachytává klik mimo) -->
        <Transition name="np-scrim">
            <div
                v-if="open"
                class="fixed inset-0 z-[130]"
                @click="emit('close')"
            />
        </Transition>

        <!-- Popover -->
        <Transition name="np">
            <div
                v-if="open"
                class="fixed z-[131] bg-white border border-neutral-200 rounded-2xl shadow-[0_24px_60px_-16px_rgba(15,17,21,.28),0_4px_14px_rgba(15,17,21,.08)] overflow-hidden"
                :class="isWide ? 'w-[480px]' : 'w-[320px]'"
                :style="popoverStyle"
            >

                <!-- ── VIEW 1: výběr formuláře ── -->
                <template v-if="!selectedForm">
                    <div class="px-4 pt-4 pb-3">
                        <h4 class="text-[15px] font-bold tracking-tight text-neutral-900">{{ $t('reservations.btn_new') }}</h4>
                        <p class="text-[12px] text-neutral-400 mt-0.5">
                            {{ $t('reservations.new_res_hint') }}
                        </p>
                    </div>

                    <!-- Jeden sloupec (málo formulářů) -->
                    <div v-if="!isWide" class="px-2 pb-2 flex flex-col gap-0.5">
                        <button
                            v-for="form in forms"
                            :key="form.id"
                            class="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-xl text-left hover:bg-neutral-50 transition-colors group"
                            @click="selectForm(form)"
                        >
                            <div
                                class="w-3 h-3 rounded-[4px] flex-shrink-0"
                                :style="{ background: getFormColor(form) }"
                            />
                            <div class="flex-1 min-w-0">
                                <div class="text-[14px] font-semibold text-neutral-900 leading-tight truncate">
                                    {{ form.name }}
                                </div>
                                <div class="text-[11.5px] text-neutral-400 mt-0.5">
                                    {{ formTypeLabel(form) }}
                                </div>
                            </div>
                            <i class="pi pi-chevron-right !text-xs text-neutral-300 group-hover:text-blue-500 transition-colors flex-shrink-0"/>
                        </button>
                    </div>

                    <!-- Grid po typech (hodně formulářů) -->
                    <div v-else class="px-3 pb-2 max-h-[65vh] overflow-y-auto">
                        <div v-for="group in formGroups" :key="group.key" class="mb-2">
                            <!-- Typ header -->
                            <div class="flex items-center gap-2 px-1 pt-2 pb-1.5">
                                <span class="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                                    {{ group.label }}
                                </span>
                                <div class="flex-1 h-px bg-neutral-100"/>
                            </div>
                            <!-- 2-sloupcový grid -->
                            <div class="grid grid-cols-2 gap-0.5">
                                <button
                                    v-for="form in group.forms"
                                    :key="form.id"
                                    class="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-left hover:bg-neutral-50 transition-colors group"
                                    @click="selectForm(form)"
                                >
                                    <div
                                        class="w-3 h-3 rounded-[4px] flex-shrink-0"
                                        :style="{ background: getFormColor(form) }"
                                    />
                                    <div class="flex-1 min-w-0">
                                        <div class="text-[13px] font-semibold text-neutral-900 leading-tight truncate">
                                            {{ form.name }}
                                        </div>
                                    </div>
                                    <i class="pi pi-chevron-right !text-[10px] text-neutral-300 group-hover:text-blue-500 transition-colors flex-shrink-0"/>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="border-t border-neutral-100 px-2 py-1.5">
                        <button
                            class="flex items-center gap-2 w-full px-2.5 py-2 rounded-xl text-[13px] font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                            @click="router.visit('/app/forms')"
                        >
                            <i class="pi pi-cog !text-sm"/>
                            {{ $t('reservations.new_res_manage_forms') }}
                        </button>
                    </div>
                </template>

                <!-- ── VIEW 2: widget placeholder ── -->
                <template v-else>
                    <div class="p-7 text-center">
                        <!-- Ikona formuláře -->
                        <div
                            class="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                            :style="{ background: getFormColor(selectedForm) }"
                        >
                            <i class="pi pi-calendar !text-2xl text-white"/>
                        </div>

                        <h4 class="text-[18px] font-bold tracking-tight text-neutral-900 mb-1.5">
                            {{ selectedForm.name }}
                        </h4>
                        <p class="text-[13px] text-neutral-400 leading-relaxed mb-5">
                            {{ $t('reservations.new_res_widget_before') }}
                            <b class="text-neutral-600">{{ selectedForm.name }}</b>
                            ({{ formTypeLabel(selectedForm).toLowerCase() }}).<br/>
                            {{ $t('reservations.new_res_steps') }}
                        </p>

                        <div class="flex gap-2">
                            <button
                                class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-sm font-medium text-neutral-700 hover:border-neutral-300 bg-white transition-colors"
                                @click="goBack"
                            >
                                <i class="pi pi-chevron-left !text-xs"/>
                                {{ $t('common.back') }}
                            </button>
                            <button
                                class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                                @click="openWidget"
                            >
                                {{ $t('reservations.new_res_open_widget') }}
                                <i class="pi pi-external-link !text-xs"/>
                            </button>
                        </div>
                    </div>
                </template>

            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
/* Scrim — žádná viditelná barva, jen blokuje klikání mimo */
.np-scrim-enter-active, .np-scrim-leave-active { transition: opacity .12s; }
.np-scrim-enter-from, .np-scrim-leave-to       { opacity: 0; }

/* Popover animace */
.np-enter-active { transition: opacity .15s ease, transform .15s cubic-bezier(.25,1,.3,1); }
.np-leave-active { transition: opacity .1s ease, transform .1s ease; }
.np-enter-from   { opacity: 0; transform: translateY(-6px) scale(.97); }
.np-leave-to     { opacity: 0; transform: translateY(-4px) scale(.98); }
</style>
